import * as React from 'react';
import PropTypes from 'prop-types';
import { Global } from '@emotion/react';
import { styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { grey } from '@mui/material/colors';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import {
  Paper,
  Tabs,
  Tab,
  Slider,
  IconButton,
  Tooltip,
  InputBase,
  Grid,
  useTheme,
  Dialog,
  Typography
} from "@mui/material";
import { SketchPicker } from "react-color";
import {
  Search as SearchIcon,
  Gradient as GradientIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  Tune as TuneIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const drawerBleeding = 56;

const Root = styled('div')(({ theme }) => ({
  height: '100%',
  backgroundColor: grey[100],
  ...theme.applyStyles('dark', {
    backgroundColor: theme.palette.background.default,
  }),
}));

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[800],
  }),
}));

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  ...theme.applyStyles('dark', {
    backgroundColor: grey[900],
  }),
}));

const predefinedColors = [
  "#FFFFFF",
  "#2A2A2A",
  "#1E1E1E",
  "#121212",
  "#FF4C4C",
  "#4CAF50",
  "#2196F3",
  "#FFD700",
  "#9C27B0",
  "#00BCD4",
  "#E91E63",
];

const predefinedGradients = [
  "linear-gradient(45deg, #2c3e50, #3498db)",
  "linear-gradient(135deg, #1a1a1a, #404040)",
  "linear-gradient(45deg, #232526, #414345)",
  "linear-gradient(45deg, #000000, #434343)",
];

function SwipeableEdgeDrawer({
  window,
  activeTab,
  setActiveTab,
  backgroundColor,
  setBackgroundColor,
  setBackgroundImage,
  searchQuery,
  setSearchQuery,
  unsplashImages,
  editSettings,
  setEditSettings,
  handleSearchUnsplash,
  currentPage,
  totalPages,
}) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [selectedGradient, setSelectedGradient] = React.useState("");
  const [customGradient, setCustomGradient] = React.useState("");

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const container = window !== undefined ? () => window().document.body : undefined;

  const isValidGradient = (value) => {
    return /^(linear|radial|conic)-gradient\(.*\)$/.test(value);
  };

  const handleGradientApply = () => {
    if (isValidGradient(customGradient)) {
      setBackgroundImage(customGradient);
      setBackgroundColor("transparent");
      setSelectedGradient(customGradient);
    }
  };

  const handlePageChange = (newPage) => {
    handleSearchUnsplash(newPage);
  };

  React.useEffect(() => {
    if (activeTab === 1 && !searchQuery) {
      setSearchQuery("dark abstract");
      handleSearchUnsplash(1);
    }
  }, [activeTab]);

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(50% - ${drawerBleeding}px)`,
            overflow: 'visible',
          },
        }}
      />
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <StyledBox
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            right: 0,
            left: 0,
            background:"#0f172a"
          }}
        >
          <Puller />
          <Typography sx={{ p: 2, color: 'white' }}>Design Tools</Typography>
        </StyledBox>
        <StyledBox sx={{ height: '100%', overflow: 'auto' }}>
          <Paper sx={{
            backgroundColor: "#121212",
            color: "#ffffff",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="fullWidth"
              indicatorColor="secondary"
              textColor="inherit"
              sx={{
                '& .MuiTabs-indicator': { height: 3 },
                background: "linear-gradient(145deg, #1a1a1a, #0d0d0d)",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-selected': {
                    color: '#fff',
                  }
                },
              }}
            >
              <Tab icon={<PaletteIcon sx={{ fontSize: "1.2rem", color: "inherit" }}/>} label="Background" />
              <Tab icon={<ImageIcon sx={{ fontSize: "1.2rem", color: "inherit" }}/>} label="Image" />
              <Tab icon={<TuneIcon sx={{ fontSize: "1.2rem", color: "inherit" }} />} label="Edit" />
            </Tabs>

            <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
              {/* Background Tab */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", display: 'block', mb: 1 }}>
                    Color Presets
                  </Typography>
                  <Grid container spacing={1}>
                    {predefinedColors.map((color) => (
                      <Grid item xs={3} key={color}>
                        <motion.div whileHover={{ scale: 1.05 }}>
                          <Paper
                            sx={{
                              height: 40,
                              backgroundColor: color,
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: color === backgroundColor ? `2px solid ${theme.palette.secondary.main}` : "none",
                            }}
                            onClick={() => {
                              setBackgroundColor(color);
                              setBackgroundImage(null);
                              setSelectedGradient("");
                            }}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)" }}>
                        Custom Color
                      </Typography>
                      <Tooltip title="Custom Color Picker">
                        <IconButton
                          size="small"
                          onClick={() => setShowColorPicker(true)}
                          sx={{ color: 'rgba(255,255,255,0.6)' }}
                        >
                          <GradientIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Dialog
                      open={showColorPicker}
                      onClose={() => setShowColorPicker(false)}
                      PaperProps={{
                        sx: {
                          background: "black",
                          boxShadow: "none",
                          overflow: "hidden",
                        },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <SketchPicker
                          color={backgroundColor}
                          onChangeComplete={(color) => {
                            setBackgroundColor(color.hex);
                            setBackgroundImage(null);
                            setSelectedGradient("");
                          }}
                        />
                        <IconButton
                          onClick={() => setShowColorPicker(false)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "#fff",
                            background: "rgba(0,0,0,0.4)",
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Dialog>
                  </Box>

                  <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", display: 'block', mb: 1 }}>
                    Gradient Presets
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {predefinedGradients.map((gradient) => (
                      <Grid item xs={6} key={gradient}>
                        <motion.div whileHover={{ scale: 1.02 }}>
                          <Paper
                            sx={{
                              height: 80,
                              background: gradient,
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: gradient === selectedGradient ? `2px solid ${theme.palette.secondary.main}` : "none",
                            }}
                            onClick={() => {
                              setBackgroundImage(gradient);
                              setBackgroundColor("transparent");
                              setSelectedGradient(gradient);
                            }}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  <InputBase
                    fullWidth
                    placeholder="linear-gradient(45deg, #ff0000, #00ff00)"
                    value={customGradient}
                    onChange={(e) => setCustomGradient(e.target.value)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      mb: 1,
                      color: "#fff",
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGradientApply}
                    startIcon={<CheckIcon />}
                    sx={{
                      background: "linear-gradient(45deg, #2196F3, #4CAF50)",
                      fontWeight: "bold",
                    }}
                  >
                    Apply Gradient
                  </Button>
                </Box>
              )}

              {/* Image Tab */}
              {activeTab === 1 && (
                <Box>
                  <InputBase
                    fullWidth
                    placeholder="Search Unsplash..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchUnsplash(1)}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      color: "#fff",
                      mb: 2,
                    }}
                    endAdornment={
                      <IconButton
                        onClick={() => handleSearchUnsplash(1)}
                        sx={{ color: 'rgba(255,255,255,0.6)' }}
                      >
                        <SearchIcon />
                      </IconButton>
                    }
                  />

                  <Grid container spacing={2} sx={{ height: 400, overflowY: 'auto' }}>
                    {unsplashImages.map((img) => (
                      <Grid item xs={6} key={img.id}>
                        <motion.div whileHover={{ scale: 1.03 }}>
                          <Paper
                            sx={{
                              position: "relative",
                              paddingTop: "100%",
                              borderRadius: "8px",
                              overflow: "hidden",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setBackgroundImage(img.urls.regular);
                              setBackgroundColor("transparent");
                            }}
                          >
                            <img
                              src={img.urls.thumb}
                              alt={img.alt_description}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Paper>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 1 }}>
                    <Button
                      startIcon={<ChevronLeft />}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      sx={{ color: '#fff' }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      Page {currentPage} of {totalPages}
                    </Typography>
                    <Button
                      endIcon={<ChevronRight />}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      sx={{ color: '#fff' }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Edit Tab */}
              {activeTab === 2 && (
                <Box>
                  {Object.entries(editSettings).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 3 }}>
                      <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", display: 'block' }}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </Typography>
                      <Slider
                        value={value}
                        onChange={(e, v) => setEditSettings(prev => ({ ...prev, [key]: v }))}
                        min={0}
                        max={200}
                        sx={{
                          color: theme.palette.secondary.main,
                          '& .MuiSlider-thumb': {
                            boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </StyledBox>
      </SwipeableDrawer>
    </Root>
  );
}

SwipeableEdgeDrawer.propTypes = {
  window: PropTypes.func,
  activeTab: PropTypes.number.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  setBackgroundColor: PropTypes.func.isRequired,
  setBackgroundImage: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  unsplashImages: PropTypes.array.isRequired,
  editSettings: PropTypes.object.isRequired,
  setEditSettings: PropTypes.func.isRequired,
  handleSearchUnsplash: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
};

export default SwipeableEdgeDrawer;